import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { adminReplyApi, FilterState, ReplyData, ReplyListResponse } from '../../../services/admin/adminReplyApi';
import { getPostCategories, PostCategory } from '../../../services/admin/postCategoryApi';
import { AdminPage, AdminPageHeader, AdminPageTitle, AdminSection } from '../common/AdminPrimitives';
import {
  ActionButton,
  ActionButtons,
  BodyCell,
  CellText,
  EmptyText,
  HeaderCell,
  HeaderText,
  RetryButton,
  TableBody,
  TableContainer,
  TableHeader,
  TableHeaderRow,
  TableInner,
  TableRow,
} from '../common/AdminTablePrimitives';
import {
  CloseButton,
  ModalBody,
  ModalCard,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  PrimaryButton,
  SecondaryButton,
} from '../common/AdminModalPrimitives';
import AdminDateFilterModal from '../common/AdminDateFilterModal';
import {
  DateButton,
  DateButtonIcon,
  DateButtonText,
} from '../common/AdminFilterPrimitives';
import AdminFilterDropdown from '../common/AdminFilterDropdown';
import CommunityTablePagination from '../community/CommunityTablePagination';

const ReplyManagement: React.FC = () => {
  const navigate = useNavigate();
  const [replies, setReplies] = useState<ReplyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<PostCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [showDateModal, setShowDateModal] = useState(false);
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pagination, setPagination] = useState({ page: 0, size: 10, totalPages: 0, totalElements: 0 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReplyId, setDeleteReplyId] = useState<number | null>(null);

  // 초기 로딩 여부 추적 (첫 번째 fetch 완료 전까지만 true)
  const isFirstLoad = useRef(true);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await getPostCategories();
      setCategories(response.data);
    } catch {}
  }, []);

  const fetchReplies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const filter: FilterState = {
        postCategoryId: selectedCategoryId || null,
        postId: null,
        fromDate: appliedStartDate || '',
        toDate: appliedEndDate || '',
        status: selectedStatus === 'true' ? true : selectedStatus === 'false' ? false : null,
        searchTerm: searchQuery || '',
      };
      const response: ReplyListResponse = await adminReplyApi.getReplies(filter, {
        page: currentPage,
        size: pagination.size,
      });
      setReplies(response.data.content || []);
      setPagination((prev) => ({
        ...prev,
        page: response.data.number ?? currentPage,
        totalPages: response.data.totalPages || 0,
        totalElements: response.data.totalElements || 0,
      }));
    } catch (err: any) {
      setError(err.message || '댓글 목록을 불러오는데 실패했습니다.');
    } finally {
      isFirstLoad.current = false;
      setLoading(false);
    }
  }, [appliedEndDate, appliedStartDate, currentPage, pagination.size, searchQuery, selectedCategoryId, selectedStatus]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchReplies();
  }, [fetchReplies]);

  const handleDeleteConfirm = async () => {
    if (!deleteReplyId) {
      return;
    }
    try {
      await adminReplyApi.deleteReply(deleteReplyId);
      setShowDeleteModal(false);
      setDeleteReplyId(null);
      fetchReplies();
    } catch (err: any) {
      setError(err.message || '댓글 삭제에 실패했습니다.');
    }
  };

  const handleHideToggle = async (replyId: number) => {
    try {
      const reply = replies.find((item) => item.reply_id === replyId);
      if (!reply) {
        return;
      }
      await adminReplyApi.toggleReplyExposure(replyId, !reply.exposed);
      fetchReplies();
    } catch (err: any) {
      setError(err.message || '댓글 상태 변경에 실패했습니다.');
    }
  };

  const stripHtmlTags = (html: string) => html.replace(/<[^>]*>/g, '').trim();

  const isInitialLoading = loading && isFirstLoad.current;
  if (isInitialLoading) {
    return (
      <AdminPage>
        <AdminSection>
          <StateMessage>로딩 중...</StateMessage>
        </AdminSection>
      </AdminPage>
    );
  }

  if (error && replies.length === 0) {
    return (
      <AdminPage>
        <AdminPageHeader>
          <AdminPageTitle>댓글 관리</AdminPageTitle>
        </AdminPageHeader>
        <AdminSection>
          <StateMessage>{error}</StateMessage>
          <RetryButton onClick={fetchReplies}>다시 시도</RetryButton>
        </AdminSection>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader>
        <AdminPageTitle>댓글 관리</AdminPageTitle>
      </AdminPageHeader>
      <AdminSection>
        <FilterRow>
          <FilterGroup>
            <AdminFilterDropdown
              value={selectedCategoryId || ''}
              onChange={(value) => { setSelectedCategoryId(value ? Number(value) : undefined); setCurrentPage(0); }}
              maxWidth="180px"
              options={[
                { value: '', label: '카테고리' },
                ...categories.map((category) => ({ value: category.id, label: category.name })),
              ]}
            />
            <DateButton type="button" onClick={() => { setTempStartDate(appliedStartDate); setTempEndDate(appliedEndDate); setShowDateModal(true); }}>
              <DateButtonText>
                {appliedStartDate && appliedEndDate ? `${appliedStartDate} ~ ${appliedEndDate}` : appliedStartDate ? `${appliedStartDate} ~` : '작성일'}
              </DateButtonText>
              <DateButtonIcon width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M4.17 5.25L7 8.08L9.83 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </DateButtonIcon>
            </DateButton>
            <AdminFilterDropdown
              value={selectedStatus}
              onChange={(value) => { setSelectedStatus(value); setCurrentPage(0); }}
              maxWidth="140px"
              options={[
                { value: '', label: '상태' },
                { value: 'true', label: '게시 중' },
                { value: 'false', label: '숨김' },
              ]}
            />
          </FilterGroup>
          <SearchInput
            type="text"
            placeholder="검색어를 입력해주세요."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setCurrentPage(0);
                fetchReplies();
              }
            }}
          />
        </FilterRow>
        <TableContainer>
          <TableInner>
            <TableHeader>
              <TableHeaderRow $columns="60px minmax(150px, 3fr) minmax(110px, 2.2fr) minmax(55px, 1.1fr) 110px 130px">
                <HeaderCell><HeaderText>번호</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>내용</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>원문 게시글</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>작성자</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>작성일</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>관리</HeaderText></HeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {replies.length > 0 ? replies.map((reply, index) => (
                <TableRow key={reply.reply_id} $columns="60px minmax(150px, 3fr) minmax(110px, 2.2fr) minmax(55px, 1.1fr) 110px 130px">
                  <BodyCell><CellText>{currentPage * pagination.size + index + 1}</CellText></BodyCell>
                  <BodyCell>
                    <LinkText $clickable={!!reply.post_id} onClick={() => reply.post_id && navigate(`/community/post/${reply.post_id}`)} title={stripHtmlTags(reply.content)}>
                      {stripHtmlTags(reply.content).length > 50 ? `${stripHtmlTags(reply.content).slice(0, 50)}...` : stripHtmlTags(reply.content) || '-'}
                    </LinkText>
                  </BodyCell>
                  <BodyCell>
                    <LinkText $clickable={!!reply.post_id} onClick={() => reply.post_id && navigate(`/community/post/${reply.post_id}`)} title={reply.post_title}>
                      {reply.post_title && reply.post_title.length > 30 ? `${reply.post_title.slice(0, 30)}...` : reply.post_title || '-'}
                    </LinkText>
                  </BodyCell>
                  <BodyCell><CellText>{reply.reply_writer_name}</CellText></BodyCell>
                  <BodyCell><CellText>{new Date(reply.reply_created_at).toLocaleDateString('ko-KR')}</CellText></BodyCell>
                  <BodyCell>
                    <ActionButtons>
                      <ActionButton $tone="danger" onClick={() => { setDeleteReplyId(reply.reply_id); setShowDeleteModal(true); }}>삭제</ActionButton>
                      <ActionButton $tone="primary" onClick={() => handleHideToggle(reply.reply_id)}>{reply.exposed ? '숨기기' : '숨김 취소'}</ActionButton>
                    </ActionButtons>
                  </BodyCell>
                </TableRow>
              )) : <EmptyRow><EmptyText>등록된 댓글이 없습니다.</EmptyText></EmptyRow>}
            </TableBody>
          </TableInner>
          <CommunityTablePagination
            currentPage={currentPage + 1}
            totalPages={pagination.totalPages}
            onPageChange={(page) => setCurrentPage(page - 1)}
          />
        </TableContainer>
      </AdminSection>
      {showDeleteModal && (
        <ConfirmModal
          title="댓글 삭제"
          description="삭제한 댓글은 복구할 수 없습니다."
          confirmText="삭제"
          onCancel={() => { setShowDeleteModal(false); setDeleteReplyId(null); }}
          onConfirm={handleDeleteConfirm}
        />
      )}
      {showDateModal && (
        <AdminDateFilterModal
          title="작성일 선택"
          startDate={tempStartDate}
          endDate={tempEndDate}
          onStartDateChange={setTempStartDate}
          onEndDateChange={setTempEndDate}
          onCancel={() => setShowDateModal(false)}
          onConfirm={() => {
            setAppliedStartDate(tempStartDate);
            setAppliedEndDate(tempEndDate);
            setCurrentPage(0);
            setShowDateModal(false);
          }}
        />
      )}
    </AdminPage>
  );
};

const ConfirmModal: React.FC<{ title: string; description: string; confirmText: string; onCancel: () => void; onConfirm: () => void }> = ({ title, description, confirmText, onCancel, onConfirm }) => (
  <ModalOverlay>
    <ModalCard $maxWidth="400px">
      <ModalHeader><ModalTitle>{title}</ModalTitle><CloseButton onClick={onCancel}>×</CloseButton></ModalHeader>
      <ModalBody><ModalText>{description}</ModalText></ModalBody>
      <ModalFooter><SecondaryButton onClick={onCancel}>취소</SecondaryButton><PrimaryButton onClick={onConfirm}>{confirmText}</PrimaryButton></ModalFooter>
    </ModalCard>
  </ModalOverlay>
);

const FilterRow = styled.div`display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap;`;
const FilterGroup = styled.div`display:flex;align-items:flex-start;gap:8px;flex-wrap:wrap;`;
const SearchInput = styled.input`
  flex: 2;
  min-width: 200px;
  height: 36px;
  padding: 8px 12px;
  border: 1px solid rgba(112,115,124,.22);
  border-radius: 12px;
  background: #fff;
  color: #171719;
  font-size: 12px;
  font-family: 'Pretendard Variable',sans-serif;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #000; }
  &::placeholder { color: rgba(55,56,60,.28); font-size: 12px; }
  @media (max-width: 768px) { min-width: 100%; }
`;
const LinkText = styled.span<{ $clickable: boolean }>`display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:${({ $clickable }) => ($clickable ? '#448181' : '#171719')};cursor:${({ $clickable }) => ($clickable ? 'pointer' : 'default')};`;
const EmptyRow = styled.div`padding:48px 20px;display:flex;justify-content:center;`;
const StateMessage = styled.div`padding:40px;text-align:center;color:#666;font-size:16px;`;
const ModalText = styled.p`margin:0;color:#171719;font-size:16px;line-height:24px;`;
export default ReplyManagement;
